import React, { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { Input, Label } from 'reactstrap';
import { Tabs, Tab, Slider, TextField, FormControlLabel, Switch } from '@mui/material';
import { getEmailSettings } from '../../store/Integration';
import './EmailSettingsModal.css';
import { color } from '../../../node_modules/@mui/system/index';

const DEFAULT_SETTINGS = {
    warmup: { enabled: false, paused: false, increasePerDay: 2, replyRate: 30, dailyLimit: 40, readEmulation: true, warmupTag: 'warmup', weekdaysOnly: false, warmCustomTrackingDomain: false, openRate: 100, spamProtection: 100, markImportant: 100 },
    campaign: { dailySendingLimit: 50 },
    stats: { sent: 0, received: 0, savedFromSpam: 0, inboxRate: 0, openRate: 0, replyRate: 0, lastUpdated: null }
};

// Toggle Button Group Component
const ToggleButtonGroup = ({ value, onChange, labels = ['Disable', 'Enable'] }) => (
    <div className="esm-toggle-btn-group">
        <button type="button" onClick={() => onChange(false)} className={`esm-toggle-btn ${!value ? 'active' : ''}`}>{labels[0]}</button>
        <button type="button" onClick={() => onChange(true)} className={`esm-toggle-btn ${value ? 'active' : ''}`}>{labels[1]}</button>
    </div>
);

const EmailSettingsModal = ({ isOpen, toggle, integration }) => {
    const dispatch = useDispatch();
    const { emailSettings } = useSelector(state => state['Integration']);
    const [activeTab, setActiveTab] = useState(0);
    const [settings, setSettings] = useState(DEFAULT_SETTINGS);
    const [saving, setSaving] = useState(false);
    const [showAdvanced, setShowAdvanced] = useState(false);

    // Load settings when panel opens
    useEffect(() => {
        if (isOpen && integration?.['_id']) dispatch(getEmailSettings(integration['_id']));
    }, [isOpen, integration?.['_id'], dispatch]);

    // Populate settings from Redux
    useEffect(() => {
        if (emailSettings?.['data']?.[integration?.['_id']]) {
            const stored = emailSettings['data'][integration['_id']];
            setSettings({
                warmup: { ...DEFAULT_SETTINGS['warmup'], ...(stored['warmup'] || {}) },
                campaign: { ...DEFAULT_SETTINGS['campaign'], ...(stored['campaign'] || {}) },
                stats: { ...DEFAULT_SETTINGS['stats'], ...(stored['stats'] || {}) }
            });
        }
    }, [emailSettings, integration?.['_id']]);

    const updateWarmup = (key, value) => setSettings(prev => ({ ...prev, warmup: { ...prev['warmup'], [key]: value } }));
    const updateCampaign = (key, value) => setSettings(prev => ({ ...prev, campaign: { ...prev['campaign'], [key]: value } }));

    const handleSave = () => {
        setSaving(true);
        dispatch({
            type: 'UPDATE_EMAIL_SETTINGS',
            payload: { integrationId: integration['_id'], settings: { warmup: settings['warmup'], campaign: settings['campaign'] }, onSuccess: () => { setSaving(false); toggle(); } }
        });
        setTimeout(() => setSaving(false), 3000);
    };

    // Warmup control handlers
    const handleEnableWarmup = () => updateWarmup('enabled', true);
    const handleDisableWarmup = () => updateWarmup('enabled', false);
    const handlePauseWarmup = () => updateWarmup('paused', !settings['warmup']['paused']);

    const accountEmail = integration?.['tokens']?.['email'] || integration?.['accountIdentifier'] || integration?.['name'] || 'Email Account';

    // Get warmup status text
    const getWarmupStatus = () => {
        if (!settings['warmup']['enabled']) return { text: 'Disabled', color: 'disabled' };
        if (settings['warmup']['paused']) return { text: 'Paused', color: 'paused' };
        return { text: 'Active', color: 'active' };
    };
    const warmupStatus = getWarmupStatus();

    if (!isOpen) return null;

    return (
        <div className="email-settings-modal">
            {/* Backdrop */}
            <div className="esm-backdrop" onClick={toggle}></div>

            {/* Slider Panel */}
            <div className={`esm-slider-panel ${isOpen ? 'open' : ''}`}>
                {/* Header */}
                <div className="esm-panel-header">
                    <div className="esm-header-content">
                        <i className="icon">
                            <svg width="18" height="13" viewBox="0 0 18 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path d="M11.6632 7.07544L10.1556 8.58807C9.5443 9.20155 8.46866 9.21477 7.8441 8.58807L6.3365 7.07544L0.921875 12.5076C1.12343 12.6008 1.34565 12.6565 1.5819 12.6565H16.4178C16.6541 12.6565 16.8762 12.6009 17.0777 12.5077L11.6632 7.07544Z" fill="#120348" />
                                <path d="M16.418 0H1.58203C1.34578 0 1.12356 0.0556172 0.922078 0.148816L6.70799 5.95392C6.70837 5.95431 6.70883 5.95438 6.70922 5.95477C6.70946 5.95502 6.70962 5.95534 6.70968 5.95568L8.59099 7.84322C8.79082 8.04305 9.20925 8.04305 9.40908 7.84322L11.29 5.956C11.29 5.956 11.2905 5.95515 11.2909 5.95477C11.2909 5.95477 11.2917 5.95431 11.2921 5.95392L17.0779 0.148781C16.8764 0.055547 16.6542 0 16.418 0ZM0.168258 0.886359C0.0639844 1.09723 0 1.3313 0 1.58203V11.0742C0 11.325 0.0639141 11.559 0.168223 11.7699L5.59223 6.3283L0.168258 0.886359ZM17.8317 0.886289L12.4078 6.3283L17.8317 11.77C17.936 11.5591 18 11.325 18 11.0742V1.58203C18 1.33123 17.936 1.09716 17.8317 0.886289Z" fill="#120348" />
                            </svg>
                        </i>
                        <span>{accountEmail}</span>
                    </div>
                    <button className="esm-close-btn" onClick={toggle}>
                        <i className="fa fa-times"></i>
                    </button>
                </div>

                {/* Stats Section - Always visible */}
                <div className="esm-stats-section">
                    <div className="esm-stats-row">
                        <div className="esm-stat-item">
                            <span className="esm-stat-number">{settings['stats']['sent']}</span>
                            <span className="esm-stat-text">Emails Sent</span>
                        </div>
                        <div className="esm-stat-item">
                            <span className="esm-stat-number">{settings['stats']['received']}</span>
                            <span className="esm-stat-text">Emails Received</span>
                        </div>
                        <div className="esm-stat-item">
                            <span className="esm-stat-number">{settings['stats']['savedFromSpam']}</span>
                            <span className="esm-stat-text">Saved from Spam</span>
                        </div>
                    </div>
                </div>

                {/* Warmup Status & Controls */}
                <div className="esm-warmup-controls">
                    <div className="esm-warmup-status">
                        <span className="esm-status-label">Warmup Status:</span>
                        <span className={`esm-status-badge ${warmupStatus['color']}`}>{warmupStatus['text']}</span>
                    </div>
                    <div className="esm-warmup-buttons">
                        {!settings['warmup']['enabled'] ? (
                            <button className="btn-style-new enable" onClick={handleEnableWarmup}>
                                <i className="fa fa-play icon"></i> Enable
                            </button>
                        ) : (
                            <>
                                <button className="btn-style-new pause" onClick={handlePauseWarmup}>
                                    <i className={`fa fa-${settings['warmup']['paused'] ? 'play' : 'pause'} icon`}></i>
                                    {settings['warmup']['paused'] ? 'Resume' : 'Pause'}
                                </button>
                                <button className="btn-style-new disable" onClick={handleDisableWarmup}>
                                    <i className="fa fa-stop icon"></i> Disable
                                </button>
                            </>
                        )}
                    </div>
                </div>

                {/* Tabs */}
                <Tabs value={activeTab} onChange={(e, v) => setActiveTab(v)} className="esm-tabs">
                    <Tab label="Warmup" />
                    <Tab label="Settings" />
                </Tabs>

                {/* Panel Content */}
                <div className="esm-panel-content">
                    {/* Warmup Tab */}
                    {activeTab === 0 && (
                        <div className="esm-tab-content">
                            {/* Basic Warmup Settings Section */}
                            <div className="esm-basic-section">
                                <div className="esm-section-header">
                                    <i className="fa fa-sliders"></i>
                                    <span>Warmup Configuration</span>
                                </div>

                                {/* Warmup Settings - Show regardless of enabled state */}
                                <div className="esm-slider-grid">
                                    <div className="esm-slider-item">
                                        <h4>Increase per day: {settings['warmup']['increasePerDay']}</h4>
                                        <TextField
                                            fullWidth
                                            hiddenLabel
                                            type="number"
                                            name="increasePerDay"
                                            value={settings['warmup']['increasePerDay']}
                                            onChange={(e) =>
                                                updateWarmup('increasePerDay', Number(e.target.value))
                                            }
                                            placeholder="Enter value"
                                            size="small"
                                            inputProps={{ min: 1, max: 10 }}
                                        />
                                    </div>
                                    <div className="esm-slider-item">
                                        <h4>Reply rate: {settings['warmup']['replyRate']}%</h4>
                                        <TextField
                                            fullWidth
                                            hiddenLabel
                                            type="number"
                                            name="replyRate"
                                            value={settings['warmup']['replyRate']}
                                            onChange={(e) =>
                                                updateWarmup('replyRate', v)
                                            }
                                            placeholder="Enter value"
                                            size="small"
                                            inputProps={{ min: 10, max: 100 }}
                                        />
                                    </div>
                                    <div className="esm-slider-item">
                                        <h4>Daily warmup limit: {settings['warmup']['dailyLimit']}</h4>
                                        <TextField
                                            fullWidth
                                            hiddenLabel
                                            type="number"
                                            name="replyRate"
                                            value={settings['warmup']['dailyLimit']}
                                            onChange={(e) =>
                                                updateWarmup('dailyLimit', v)
                                            }
                                            placeholder="Enter value"
                                            size="small"
                                            inputProps={{ min: 5, max: 100 }}
                                        />
                                    </div>
                                </div>

                                <div className="esm-slider-grid style-two">
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-info">
                                            <h4>Read emulation</h4>
                                            <p>Simulate reading emails to improve engagement metrics</p>
                                        </div>
                                        {/* <ToggleButtonGroup value={settings['warmup']['readEmulation']} onChange={(v) => updateWarmup('readEmulation', v)} /> */}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings?.warmup?.readEmulation || false}
                                                    onChange={(e) =>
                                                        updateWarmup('readEmulation', e.target.checked)
                                                    }
                                                    size="small"
                                                    className="csetup-switch"
                                                />
                                            }
                                        />
                                    </div>
                                </div>
                                <div className="esm-slider-grid style-two">
                                    <div className="esm-slider-item">
                                        <div className="esm-slider-info">
                                            <h4>Warmup tag (for filtering)</h4>
                                        </div>
                                        <TextField
                                            fullWidth
                                            hiddenLabel
                                            type="text"
                                            name="warmup"
                                            value={settings['warmup']['warmupTag']}
                                            onChange={(e) => updateWarmup('warmupTag', e.target.value)}
                                            placeholder="warmup"
                                            size="small"
                                            inputProps={{ min: 5, max: 100 }}
                                        />
                                    </div>
                                </div>
                            </div>

                            {/* Advanced Settings Toggle Button */}
                            <button type="button" onClick={() => setShowAdvanced(!showAdvanced)} className="esm-advanced-toggle">
                                <i className="icon">
                                    <svg width="11" height="11" viewBox="0 0 11 11" fill="none" xmlns="http://www.w3.org/2000/svg">
                                        <path d="M9.62161 3.98472L8.82416 3.88334C8.75914 3.68303 8.67844 3.48815 8.5828 3.30052L9.07527 2.66638C9.17149 2.54462 9.21942 2.39165 9.20987 2.23676C9.20032 2.08187 9.13397 1.93594 9.02352 1.82693L8.35544 1.15885C8.24638 1.04774 8.10013 0.980792 7.94475 0.970851C7.78937 0.96091 7.63579 1.00867 7.51345 1.10498L6.88015 1.59745C6.69241 1.50174 6.49739 1.42103 6.29691 1.35609L6.19553 0.559914C6.17707 0.405668 6.10275 0.263527 5.98661 0.160359C5.87046 0.0571912 5.72055 0.000143841 5.5652 0L4.61505 0C4.29395 0 4.0229 0.240508 3.98472 0.558641L3.88334 1.35609C3.6828 1.42085 3.48776 1.50156 3.3001 1.59745L2.66638 1.10498C2.54455 1.00894 2.39162 0.961126 2.23678 0.970671C2.08194 0.980217 1.93604 1.04645 1.82693 1.15673L1.15885 1.82439C1.04762 1.93348 0.980604 2.0798 0.970661 2.23528C0.960719 2.39076 1.00855 2.54443 1.10498 2.6668L1.59745 3.30052C1.50164 3.48807 1.42093 3.68296 1.35609 3.88334L0.559914 3.98472C0.240508 4.0229 0 4.29395 0 4.61505V5.5652C0 5.88631 0.240508 6.15736 0.558641 6.19553L1.35609 6.29691C1.42184 6.49924 1.50286 6.69436 1.59745 6.87973L1.10498 7.51388C1.00876 7.63563 0.960833 7.7886 0.970382 7.94349C0.97993 8.09838 1.04628 8.24431 1.15673 8.35332L1.82481 9.0214C1.93403 9.13228 2.08027 9.19903 2.2356 9.20889C2.39093 9.21875 2.54444 9.17103 2.6668 9.07485L3.30052 8.58238C3.48589 8.67739 3.68101 8.75841 3.88334 8.82373L3.98472 9.61949C4.0229 9.93974 4.29395 10.1803 4.61505 10.1803H5.5652C5.88631 10.1803 6.15736 9.93974 6.19553 9.62161L6.29691 8.82416C6.49722 8.75914 6.6921 8.67844 6.87973 8.5828L7.51388 9.07527C7.7705 9.27463 8.13275 9.2513 8.35332 9.02352L9.0214 8.35544C9.13251 8.24638 9.19946 8.10013 9.2094 7.94475C9.21934 7.78937 9.17158 7.63579 9.07527 7.51345L8.5828 6.87973C8.67782 6.69436 8.75883 6.49924 8.82416 6.29691L9.61992 6.19553C9.77416 6.17707 9.9163 6.10275 10.0195 5.98661C10.1226 5.87046 10.1797 5.72055 10.1798 5.5652V4.61505C10.1799 4.45991 10.1232 4.31011 10.0203 4.19397C9.91746 4.07783 9.77562 4.00339 9.62161 3.98472ZM5.09013 7.21101C3.92067 7.21101 2.96924 6.25958 2.96924 5.09013C2.96924 3.92067 3.92067 2.96924 5.09013 2.96924C6.25958 2.96924 7.21101 3.92067 7.21101 5.09013C7.21101 6.25958 6.25958 7.21101 5.09013 7.21101Z" fill="#4B5563" />
                                    </svg>
                                </i>
                                {showAdvanced ? 'Hide' : 'Show'} advanced settings
                                <i className={`fa fa-chevron-${showAdvanced ? 'up' : 'down'}`}></i>
                            </button>

                            {/* Advanced Settings Section */}
                            {showAdvanced && (
                                <div className="esm-advanced-section">
                                    <div className="esm-advanced-header">
                                        <i className="fa fa-fire"></i>
                                        <h4>Warmup Settings Advanced</h4>
                                    </div>

                                    {/* Weekdays only */}
                                    <div className="esm-advanced-row">
                                        <div className="esm-advanced-info">
                                            <h5 className="esm-advanced-label">Weekdays only</h5>
                                            <p className="esm-advanced-desc">Only send warmup emails on weekdays for a more natural sending pattern</p>
                                        </div>
                                        {/* <ToggleButtonGroup value={settings['warmup']['weekdaysOnly']} onChange={(v) => updateWarmup('weekdaysOnly', v)} /> */}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={settings?.warmup?.weekdaysOnly || false}
                                                    onChange={(e) =>
                                                        updateWarmup('weekdaysOnly', e.target.checked)
                                                    }
                                                    size="small"
                                                    className="csetup-switch"
                                                />
                                            }
                                        />
                                    </div>

                                    {/* Read emulation */}
                                    <div className="esm-advanced-row">
                                        <div className="esm-advanced-info">
                                            <h5 className="esm-advanced-label">Read emulation</h5>
                                            <p className="esm-advanced-desc">Spend time and scroll through your warmup email to emulate human-like reading</p>
                                        </div>
                                        {/* <ToggleButtonGroup value={settings['warmup']['readEmulation']} onChange={(v) => updateWarmup('readEmulation', v)} /> */}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(settings?.warmup?.readEmulation)}
                                                    onChange={(e) =>
                                                        updateWarmup('readEmulation', e.target.checked)
                                                    }
                                                    size="small"
                                                    className="csetup-switch"
                                                />
                                            }
                                        />
                                    </div>

                                    {/* Warm custom tracking domain */}
                                    <div className="esm-advanced-row">
                                        <div className="esm-advanced-info">
                                            <h5 className="esm-advanced-label">Warm custom tracking domain</h5>
                                            <p className="esm-advanced-desc">Include your custom tracking domain in your warmup emails to further improve deliverability</p>
                                        </div>
                                        {/* <ToggleButtonGroup value={settings['warmup']['warmCustomTrackingDomain']} onChange={(v) => updateWarmup('warmCustomTrackingDomain', v)} /> */}
                                        <FormControlLabel
                                            control={
                                                <Switch
                                                    checked={Boolean(settings?.warmup?.warmCustomTrackingDomain)}
                                                    onChange={(e) =>
                                                        updateWarmup('warmCustomTrackingDomain', e.target.checked)
                                                    }
                                                    size="small"
                                                    className="csetup-switch"
                                                />
                                            }
                                        />
                                    </div>

                                    {/* Open Rate Slider */}
                                    <div className="esm-advanced-row esm-advanced-slider-row">
                                        <div className="esm-advanced-info">
                                            <h5 className="esm-advanced-label">Open Rate</h5>
                                            <p className="esm-advanced-desc">How many of your warm up emails to open</p>
                                        </div>
                                        <div className="esm-advanced-slider-wrapper">
                                            <Slider value={settings['warmup']['openRate']} onChange={(e, v) => updateWarmup('openRate', v)} min={0} max={100} valueLabelDisplay="auto" />
                                            <span className="esm-slider-value">{settings['warmup']['openRate']}</span>
                                        </div>
                                    </div>

                                    {/* Spam Protection Slider */}
                                    <div className="esm-advanced-row esm-advanced-slider-row">
                                        <div className="esm-advanced-info">
                                            <h5 className="esm-advanced-label">Spam Protection</h5>
                                            <p className="esm-advanced-desc">How many of your warm up emails to save from spam folder</p>
                                        </div>
                                        <div className="esm-advanced-slider-wrapper">
                                            <Slider value={settings['warmup']['spamProtection']} onChange={(e, v) => updateWarmup('spamProtection', v)} min={0} max={100} valueLabelDisplay="auto" />
                                            <span className="esm-slider-value">{settings['warmup']['spamProtection']}</span>
                                        </div>
                                    </div>

                                    {/* Mark Important Slider */}
                                    <div className="esm-advanced-row esm-advanced-slider-row">
                                        <div className="esm-advanced-info">
                                            <h5 className="esm-advanced-label">Mark Important</h5>
                                            <p className="esm-advanced-desc">How many of your warm up emails to mark as important</p>
                                        </div>
                                        <div className="esm-advanced-slider-wrapper">
                                            <Slider value={settings['warmup']['markImportant']} onChange={(e, v) => updateWarmup('markImportant', v)} min={0} max={100} valueLabelDisplay="auto" />
                                            <span className="esm-slider-value">{settings['warmup']['markImportant']}</span>
                                        </div>
                                    </div>
                                </div>
                            )}
                        </div>
                    )}

                    {/* Settings Tab */}
                    {activeTab === 1 && (
                        <div className="esm-tab-content">
                            <div className="esm-advanced-row esm-advanced-slider-row">
                                <div className="esm-advanced-info">
                                    <h5 className="esm-advanced-label">Daily sending limit</h5>
                                    <p className="esm-advanced-desc">Maximum number of emails to send per day from this account</p>
                                </div>
                                <div className="esm-advanced-slider-wrapper">
                                    <Slider value={settings['campaign']['dailySendingLimit']} onChange={(e, v) => updateCampaign('dailySendingLimit', v)} min={1} max={500} valueLabelDisplay="auto" />
                                    <span className="esm-slider-value">{settings['campaign']['dailySendingLimit']}</span>
                                </div>
                            </div>
                        </div>
                    )}
                </div>

                {/* Footer */}
                <div className="esm-panel-footer">
                    <button className="btn-style-border" onClick={toggle}>
                        Cancel
                    </button>
                    <button className="btn-style-new" onClick={handleSave} disabled={saving}>
                        {saving ? <><i className="fa fa-spinner fa-spin"></i> Saving...</> : 'Save'}
                        <i className="icon ml-2">
                            <svg width="13" height="13" viewBox="0 0 13 13" fill="none" xmlns="http://www.w3.org/2000/svg">
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M5.55103 6.0199L3.8619 2.64164C3.81493 2.54733 3.79891 2.44061 3.81612 2.33667C3.83334 2.23273 3.88291 2.13687 3.95778 2.06274C4.03265 1.98862 4.129 1.94 4.23311 1.92383C4.33722 1.90765 4.44378 1.92474 4.53761 1.97265L11.5616 5.57465C11.6433 5.61681 11.7119 5.68065 11.7597 5.75919C11.8076 5.83773 11.8329 5.92792 11.8329 6.0199C11.8329 6.11187 11.8076 6.20207 11.7597 6.2806C11.7119 6.35914 11.6433 6.42299 11.5616 6.46514L4.53761 10.0671C4.44378 10.1153 4.33712 10.1326 4.23287 10.1166C4.12863 10.1005 4.03212 10.0519 3.95717 9.97767C3.88221 9.90346 3.83263 9.80745 3.81552 9.70337C3.79841 9.59929 3.81465 9.49246 3.8619 9.39816L5.55103 6.0199ZM4.52109 2.63156L6.14861 5.88716C6.19061 5.97061 6.19061 6.06918 6.14861 6.15263L4.52109 9.40824L11.1281 6.0199L4.52109 2.63156Z" fill="currentColor" />
                                <path fill-rule="evenodd" clip-rule="evenodd" d="M5.88143 6.31663C5.84245 6.31664 5.80385 6.30896 5.76784 6.29404C5.73182 6.27912 5.6991 6.25726 5.67154 6.2297C5.64397 6.20213 5.62211 6.16941 5.60719 6.1334C5.59228 6.09738 5.5846 6.05879 5.5846 6.01981C5.5846 5.98082 5.59228 5.94223 5.60719 5.90621C5.62211 5.8702 5.64397 5.83748 5.67154 5.80991C5.6991 5.78235 5.73182 5.76049 5.76784 5.74557C5.80385 5.73065 5.84245 5.72298 5.88143 5.72298L11.3705 5.72298C11.4095 5.72298 11.4481 5.73065 11.4841 5.74557C11.5201 5.76049 11.5529 5.78235 11.5804 5.80991C11.608 5.83748 11.6299 5.8702 11.6448 5.90621C11.6597 5.94223 11.6674 5.98083 11.6674 6.01981C11.6674 6.05879 11.6597 6.09738 11.6448 6.1334C11.6299 6.16941 11.608 6.20213 11.5804 6.2297C11.5529 6.25726 11.5201 6.27912 11.4841 6.29404C11.4481 6.30896 11.4095 6.31664 11.3705 6.31664L5.88143 6.31663Z" fill="currentColor" />
                            </svg>
                        </i>
                    </button>
                </div>
            </div>
        </div>
    );
};

export default EmailSettingsModal;
